/**
 * @fileoverview Civil & Criminal Offences — findings table for one head
 * @module jobs/phases/review/civilCriminalOffences/OffenceFindingsTable
 *
 * Findings grouped by respondent, with search + decision filter. Each row opens
 * the tabbed review modal. Mirrors the flagged TransactionTable shape.
 */

import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { AlertTriangle, FileQuestion, Loader2, Search } from "lucide-react";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives/badge";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { StatuteBadge } from "../flaggedTransactions/AgentText";
import { OffenceFindingModal } from "./OffenceFindingModal";
import {
  fmtMoney,
  fmtCapacity,
  humaniseTokens,
  BANDING_BADGE,
  OFFENCE_DECISION_BADGE,
  SOURCE_DATA_LABEL,
  type OffenceFindingUI,
  type OffenceHeadUI,
} from "./offenceShared";

const offences = api.jobs.summarization.agents.civilCriminalOffences;

export function OffenceFindingsTable(props: {
  jobId: Id<"jobs">;
  head: OffenceHeadUI;
  isViewer: boolean;
}) {
  const { jobId, head, isViewer } = props;
  const [search, setSearch] = useState("");
  const [decisionFilter, setDecisionFilter] = useState<string>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  // While the head is updating, the rows are populate-seeded candidate stubs the
  // agent is still characterising — make that unmistakable so the provisional
  // conduct text isn't read as the finished output.
  const isRunning = head.status === "updating";

  const { data: findings } = useQuery(
    convexQuery(offences.publicQueries.listFindings, {
      jobId,
      headKey: head.headKey as never,
    }),
  );

  const rows: OffenceFindingUI[] = useMemo(() => findings ?? [], [findings]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter(
      (f) =>
        (decisionFilter === "all" || f.decision === decisionFilter) &&
        (!s ||
          f.respondentName.toLowerCase().includes(s) ||
          f.conductSummary.toLowerCase().includes(s)),
    );
  }, [rows, search, decisionFilter]);

  // Group by respondent, preserving first-seen order, then float the most
  // critical ("flagged") individuals to the top. A respondent is ranked by the
  // single most serious signal across their findings: a criminal overlay (ASIC
  // referral risk) outranks a "Refer to ASIC" decision, which outranks a large
  // quantum / high risk, then medium. Equal severities keep first-seen order
  // (Array.prototype.sort is stable).
  const groups = useMemo(() => {
    const map = new Map<string, OffenceFindingUI[]>();
    for (const f of filtered) {
      const list = map.get(f.respondentName) ?? [];
      list.push(f);
      map.set(f.respondentName, list);
    }
    const groupSeverity = (list: OffenceFindingUI[]): number =>
      list.reduce((max, f) => {
        const s = f.criminalOverlay?.engaged
          ? 4
          : f.decision === "refer"
            ? 3
            : f.banding === "large" || f.risk === "high"
              ? 2
              : f.banding === "medium" || f.risk === "medium"
                ? 1
                : 0;
        return Math.max(max, s);
      }, 0);
    return Array.from(map.entries()).sort(
      ([, a], [, b]) => groupSeverity(b) - groupSeverity(a),
    );
  }, [filtered]);

  // Modal navigation should follow the on-screen (severity-sorted, grouped)
  // order so "next/prev" walks the findings as the practitioner sees them.
  const orderedIds = groups.flatMap(([, list]) => list.map((f) => f._id));

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/30 py-10 text-center">
        {isRunning ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-blue-600 dark:text-blue-400" />
            <p className="text-sm text-muted-foreground">
              Agent is running — characterising findings for each respondent…
            </p>
          </>
        ) : (
          <>
            <FileQuestion className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No findings yet. Re-run this head once its feeder agents have
              produced output.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {isRunning && (
        <div className="mb-2 flex items-center gap-2 rounded-md border border-blue-200/80 bg-blue-50/60 px-3 py-2 text-xs font-medium text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-blue-300">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          <span>
            Agent is running — the rows below are provisional candidates and
            will be characterised and re-scored as it works.
          </span>
        </div>
      )}
      <div className="mb-2 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search respondent or conduct…"
            className="h-8 pl-8 text-xs"
          />
        </div>
        <Select value={decisionFilter} onValueChange={setDecisionFilter}>
          <SelectTrigger className="h-8 w-40 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All decisions</SelectItem>
            {Object.entries(OFFENCE_DECISION_BADGE).map(([value, b]) => (
              <SelectItem key={value} value={value}>
                {b.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        className={cn(
          "overflow-hidden rounded-lg border transition-opacity",
          isRunning && "pointer-events-none opacity-50",
        )}
        aria-busy={isRunning}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provision</TableHead>
              <TableHead className="w-[40%]">Conduct</TableHead>
              <TableHead className="text-right">Quantum</TableHead>
              <TableHead>Banding</TableHead>
              <TableHead>Decision</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map(([respondent, list]) => (
              <Fragment key={`grp-${respondent}`}>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell colSpan={5} className="py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">
                        {respondent}
                      </span>
                      {list[0]?.respondentCapacity && (
                        <Badge variant="outline" size="sm">
                          {fmtCapacity(list[0].respondentCapacity)}
                        </Badge>
                      )}
                      <span className="text-[11px] text-muted-foreground">
                        {list.length} finding{list.length === 1 ? "" : "s"}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
                {list.map((f) => {
                  const banding = BANDING_BADGE[f.banding];
                  const decision = OFFENCE_DECISION_BADGE[f.decision];
                  return (
                    <TableRow
                      key={f._id}
                      className="cursor-pointer"
                      onClick={() => setOpenId(f._id)}
                    >
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <StatuteBadge>{f.provision}</StatuteBadge>
                          {f.criminalOverlay?.engaged && (
                            <span title={f.criminalOverlay.referralNote}>
                              <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                            </span>
                          )}
                        </div>
                        {f.limb && (
                          <div className="mt-0.5 text-[11px] text-muted-foreground">
                            {f.limb}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="line-clamp-2 text-xs text-muted-foreground">
                          {humaniseTokens(f.conductSummary)}
                        </p>
                        {f.sourceDataRequired && (
                          <span className="mt-0.5 inline-block text-[10px] font-medium text-amber-700 dark:text-amber-400">
                            {SOURCE_DATA_LABEL}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {typeof f.quantum === "number" ? (
                          <span className="font-mono text-sm tabular-nums">
                            {fmtMoney(f.quantum)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge color={banding.color} variant="soft" size="sm">
                          {banding.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          color={decision.color}
                          variant="soft"
                          size="sm"
                          className="whitespace-nowrap"
                        >
                          {decision.label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {openId && (
        <OffenceFindingModal
          jobId={jobId}
          head={head}
          findingId={openId as Id<"offenceFindings">}
          orderedIds={orderedIds}
          isViewer={isViewer}
          onClose={() => setOpenId(null)}
          onNavigate={(id) => setOpenId(id)}
        />
      )}
    </>
  );
}
